import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {ReactNode, useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinStore from 'molbio/proteins/ProteinStore';
import {ProteinStructureExternalFile, ProteinVersion} from 'molbio/proteins/util/ProteinConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import {proteinSelectionUIGlobalState} from 'toolbar/protein/ProteinUIGlobalState';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';

import {
  HasInProgressAlphaFoldPredictionQuery,
  HasInProgressAlphaFoldPredictionQueryVariables,
} from './__generated/HasInProgressAlphaFoldPredictionQuery';
import {UserVerificationStatusQuery} from './__generated/UserVerificationStatusQuery';
import {AlphaFoldPaperLink} from './AlphaFoldPaperLink';
import {
  AvailableStructureInfo,
  findCoordinateStructureById,
  getStructuresForVersion,
  getCoordinateStructureId,
  getStructureInfoFromVersions,
  USER_VERIFICATION_STATUS,
  getRunBlockingMessage,
} from './AlphaFoldTabUtils';
import RedoPredictionModal from './RedoPredictionModal';
import {CoordinateStructuresList, MetadataItem} from './structure-prediction/CoordinateStructureList';
import NewPredictionForm from './structure-prediction/NewPredictionForm';

enum ViewState {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COORDINATE_STRUCTURES_LIST = 'COORDINATE_STRUCTURES_LIST',
  RUN_STRUCTURE_PREDICTION = 'RUN_STRUCTURE_PREDICTION',
  VIEWER = 'VIEWER',
}

function AlphaFoldCitation({className}: {className?: string}): JSX.Element {
  return (
    <p className={className}>
      3D structures are predicted using&nbsp;
      <AlphaFoldPaperLink />.
    </p>
  );
}

function ViewerPane({
  structureFile,
  structureFileOutOfDate,
  predictionInProgress,
  proteinId,
  structureProteinVersionId,
  aminoAcids,
  permissions,
  selectionStart,
  selectionEnd,
  renderWarningMessage,
  onBack,
  componentIds,
  disableSelection,
}: {
  structureFile: ProteinStructureExternalFile | null;
  structureFileOutOfDate: boolean;
  predictionInProgress: boolean;
  proteinId: string;
  structureProteinVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  selectionStart: number;
  selectionEnd: number | null;
  renderWarningMessage: (message: ReactNode) => JSX.Element;
  onBack: () => void;
  componentIds: Array<string>;
  disableSelection: boolean;
}): JSX.Element {
  const previousStructureFile = usePrevious(structureFile);
  const [signedStructureURL, setSignedStructureURL] = useState<string | null>(null);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);
  useEffect(() => {
    // Check that value has actually changed and not just the object reference, otherwise this causes
    // an unnecessary re-render of the viewer because the signed URL is always unique.

    if (structureFile && !isEqual(structureFile, previousStructureFile)) {
      const fetchSignedUrl = async (): Promise<void> => {
        const signedURL = await performSignedFileQuery(
          structureFile.id,
          structureFile.url,
          proteinId,
          structureProteinVersionId
        );
        if (signedURL == null) {
          notifications.error('Error fetching structure file. Please refresh the page.');
        } else {
          setSignedStructureURL(signedURL);
        }
      };
      fetchSignedUrl();
    }
  }, [structureFile, previousStructureFile, proteinId, structureProteinVersionId]);

  if (userVerificationStatusQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (userVerificationStatusQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Error when loading structure prediction details. Please refresh the page.');
    return <></>;
  }

  if (structureFile == null) {
    return (
      <div className={css(styles.smallTabSpacing)}>
        <p className={css(styles.largestText)}>Predicting 3D structure...</p>
        <p className={css(styles.smallerText)}>
          This could take some time. You will receive an email when it’s ready.
        </p>
        <AlphaFoldCitation className={css(styles.smallerText)} />
      </div>
    );
  } else {
    const currentUser = userVerificationStatusQueryResult.data.currentUser;
    assert(
      currentUser?.__typename === 'UserNode' || currentUser == null,
      `Non-user should not access this page.`
    );

    let warningMessage: ReactNode | null = null;
    if (structureFileOutOfDate) {
      warningMessage = 'This structure is out of sync.';
      if (predictionInProgress) {
        // NOTE: this message doesn't handle the case where a different user requested the run.
        warningMessage = `${warningMessage} You’ll receive an email when your updated structure is ready.`;
      } else if (getRunBlockingMessage(aminoAcids, permissions, 1)) {
        warningMessage = `${warningMessage} ${getRunBlockingMessage(aminoAcids, permissions, 1)}`;
      } else if (
        BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC &&
        (!currentUser?.institution?.verified || !currentUser?.verified)
      ) {
        warningMessage = `${warningMessage} structure prediction is only available to verified academic users from verified institutions.`;
      } else {
        warningMessage = (
          <span>
            {warningMessage}{' '}
            <Button
              buttonStyle={ButtonStyle.LINK}
              className={css(styles.buttonLinkText)}
              text="Redo structure prediction"
              onClick={() => {
                trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_REDO_RUN);
                runModal(RedoPredictionModal, {proteinId});
              }}
            />
            .
          </span>
        );
      }
    }

    if (signedStructureURL == null) {
      return <CenteredHorizontalThrobber />;
    }

    return (
      <div className={css(styles.viewerWrapper)}>
        <div className={css(styles.topButtonsContainer)}>
          <div className={css(styles.predictionListButtonContainer)}>
            <Button
              className={css(styles.predictionListButton)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              icon="chevron-left"
              text="Prediction List"
              onClick={onBack}
            />
          </div>
          {componentIds.length > 1 && (
            <MetadataItem
              label="Multimer Components"
              value={
                <ChipList itemIds={componentIds} chipSize={ChipSize.MINI} maxChips={3} isVertical={false} />
              }
            />
          )}
        </div>
        {warningMessage && renderWarningMessage(warningMessage)}
        <MolstarViewer
          url={signedStructureURL}
          format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[structureFile.previewType]}
          className={classNames('reset', css(styles.viewer))}
          interactionAnalyticsCallback={() => {
            trackEvent(MOLBIO_ANALYTICS_ACTIONS.INTERACTED_ALPHAFOLD_VIEWER);
          }}
          residueStart={selectionStart}
          residueEnd={selectionEnd}
          disableSelection={disableSelection || structureFileOutOfDate}
        />
      </div>
    );
  }
}

export const HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION: DocumentNode = gql`
  query HasInProgressAlphaFoldPredictionQuery($proteinId: StrictString!) @skipAudit {
    protein(id: $proteinId) {
      ... on ProteinNode {
        id
        inProgressStructurePredictionRuns {
          dbId
          status
          requestedModel
          creator {
            name
          }
          createdAt
          componentIds
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AlphaFoldTabContentProps {
  proteinId: string;
}

interface AlphaFoldTabContentEnhancedProps {
  versions?: Array<ProteinVersion>;
  currentlyViewedProteinVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  currentCoordinateStructureId: string | null;
}

class AlphaFoldTabContentConnector extends DataConnector<
  AlphaFoldTabContentProps,
  AlphaFoldTabContentEnhancedProps
> {
  shouldSubscribeToRedux = false;
  stores = [ProteinStore];

  enhanceProps(): AlphaFoldTabContentEnhancedProps {
    const proteinStore = this.proteinStore();
    const protein = proteinStore.getProteinById(this.props.proteinId)!;
    const currentlyViewedProteinVersionId = protein.version.id;
    return {
      versions: proteinStore.getVersionsById(this.props.proteinId),
      currentlyViewedProteinVersionId,
      aminoAcids: protein.version.aminoAcids,
      permissions: protein.permissions,
      currentCoordinateStructureId: proteinStore.getCoordinateStructureId(currentlyViewedProteinVersionId),
    };
  }
}

function AlphaFoldTabContent({
  proteinId,
  versions,
  currentlyViewedProteinVersionId,
  aminoAcids,
  permissions,
  currentCoordinateStructureId,
}: AlphaFoldTabContentProps & AlphaFoldTabContentEnhancedProps): JSX.Element {
  const [proteinSelectionUIState] = proteinSelectionUIGlobalState.useGlobalClientState(proteinId);
  const {start: selectionStart, end: selectionEnd} = proteinSelectionUIState;
  const structureInfo = getStructureInfoFromVersions(currentlyViewedProteinVersionId, versions);
  const [viewState, setViewState] = useState<ViewState>(ViewState.LOADING);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState<boolean>(false);

  const structuresForVersion: Array<AvailableStructureInfo> = useMemo(
    () => getStructuresForVersion(versions, structureInfo.versionId || undefined),
    [versions, structureInfo.versionId]
  );

  const selectedCoordinateStructure = currentCoordinateStructureId
    ? findCoordinateStructureById(structuresForVersion, currentCoordinateStructureId)
    : null;

  const queryResult = useBenchlingQuery<
    HasInProgressAlphaFoldPredictionQuery,
    HasInProgressAlphaFoldPredictionQueryVariables
  >(HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION, {
    variables: {proteinId},
  });

  // Extract query data for dependency array to avoid complex expression
  const queryData = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : null;

  const hasPredictionInProgress =
    queryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(queryResult.data.protein) &&
    queryResult.data.protein.inProgressStructurePredictionRuns.length > 0;

  // Decide what to show upon first render after the query is loaded.
  useEffect(() => {
    if (initialLoadCompleted) {
      return;
    }

    if (queryResult.type === QueryResultType.PENDING) {
      setViewState(ViewState.LOADING);
    } else if (queryResult.type === QueryResultType.ERROR) {
      setInitialLoadCompleted(true);
      setViewState(ViewState.ERROR);
    } else if (queryResult.type === QueryResultType.SUCCESS) {
      setInitialLoadCompleted(true);
      // Determine view type based on structure availability and number of coordinate structures
      if (structuresForVersion.length > 0) {
        // If there are any coordinate structures, go directly to viewer
        setViewState(ViewState.VIEWER);
      } else if (hasPredictionInProgress) {
        setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
      } else {
        setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
      }
    }
  }, [
    queryResult.type,
    queryData,
    hasPredictionInProgress,
    initialLoadCompleted,
    structuresForVersion.length,
  ]);

  // Clear current coordinate structure when switching to coordinate structures list view
  useEffect(() => {
    if (viewState === ViewState.COORDINATE_STRUCTURES_LIST) {
      ProteinActions.setCoordinateStructureId(currentlyViewedProteinVersionId, null);
    }
  }, [viewState, currentlyViewedProteinVersionId]);

  // Render based on viewType
  switch (viewState) {
    case ViewState.LOADING:
      return <CenteredHorizontalThrobber />;

    case ViewState.ERROR:
      notifications.error('Error loading structure prediction details. Please refresh the page.');
      return <></>;

    case ViewState.COORDINATE_STRUCTURES_LIST: {
      // Extract in-progress structure predictions from query data
      const inProgressStructurePredictions =
        queryData && !isInaccessible(queryData.protein)
          ? queryData.protein.inProgressStructurePredictionRuns
          : [];

      return (
        <CoordinateStructuresList
          coordinateStructures={structuresForVersion}
          onSelectCoordinateStructure={(coordinateStructure) => {
            const coordinateStructureId = getCoordinateStructureId(coordinateStructure);
            ProteinActions.setCoordinateStructureId(currentlyViewedProteinVersionId, coordinateStructureId);
            setViewState(ViewState.VIEWER);
          }}
          onCreateRun={() => {
            setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
          }}
          inProgressStructurePredictions={inProgressStructurePredictions}
        />
      );
    }

    case ViewState.VIEWER: {
      // Use selected coordinate structure if available, otherwise fall back to structureInfo
      const viewerCoordinateStructure =
        selectedCoordinateStructure ||
        (structureInfo.structureFile
          ? {
              structureFile: structureInfo.structureFile,
              model: structureInfo.model || '',
              modelVersion: structureInfo.modelVersion || '',
              versionId: structureInfo.versionId,
              createdAt: '',
              structureFileOutOfDate: structureInfo.structureFileOutOfDate || false,
              componentIds: structureInfo.componentIds,
            }
          : null);

      if (!viewerCoordinateStructure) {
        // Fallback to loading if somehow we get into an inconsistent state
        return <CenteredHorizontalThrobber />;
      }

      // We don't support heteromer selection correctly yet, so we disable it for now
      const shouldDisableSelection = new Set(viewerCoordinateStructure.componentIds).size > 1;
      return (
        <ViewerPane
          structureFile={viewerCoordinateStructure.structureFile}
          structureFileOutOfDate={viewerCoordinateStructure.structureFileOutOfDate}
          predictionInProgress={hasPredictionInProgress}
          proteinId={proteinId}
          structureProteinVersionId={viewerCoordinateStructure.versionId}
          aminoAcids={aminoAcids}
          permissions={permissions}
          selectionStart={selectionStart}
          selectionEnd={selectionEnd}
          componentIds={viewerCoordinateStructure.componentIds}
          renderWarningMessage={(message) => (
            <div className={css(styles.smallInlineBannerMargin)}>
              <InlineBanner text={message} bannerType={InlineBannerType.WARNING} />
            </div>
          )}
          onBack={() => {
            ProteinActions.setCoordinateStructureId(currentlyViewedProteinVersionId, null);
            setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
          }}
          disableSelection={shouldDisableSelection}
        />
      );
    }

    case ViewState.RUN_STRUCTURE_PREDICTION:
      return (
        <NewPredictionForm
          aminoAcids={aminoAcids}
          permissions={permissions}
          proteinId={proteinId}
          onSubmit={() => {
            setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
          }}
          onBack={() => {
            setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
          }}
        />
      );

    default:
      return <CenteredHorizontalThrobber />;
  }
}

const styles = StyleSheet.create({
  grayDarker: {
    color: GRAY_DARKER,
  },
  smallTabSpacing: {
    margin: '16px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  imgMargin: {
    margin: '34px 0px',
  },
  modelSelector: {
    minWidth: '300px',
    textAlign: 'left',
  },
  buttonLinkText: {
    verticalAlign: 'baseline',
  },
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  predictionListButton: {
    minWidth: '120px',
  },
  predictionListButtonContainer: {
    minWidth: '130px',
  },
  topButtonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  viewer: {
    borderWidth: '0px',
    flex: '1 1 auto',
  },
  smallInlineBannerMargin: {
    margin: '4px',
  },
});

export default AlphaFoldTabContentConnector.connect(AlphaFoldTabContent);
